<template>
  <AppLayout pageTitle="Dashboard">
    <!-- Profile Update Reminder -->
    <div v-if="profileIncomplete" class="row mb-3">
      <div class="col-12">
        <div class="profile-update-banner">
          <div class="banner-icon">
            <i class="ph-duotone ph-user-circle-gear"></i>
          </div>
          <div class="flex-grow-1">
            <p class="mb-0 fw-semibold f-s-14" style="color: #92400e;">
              Your profile is incomplete. 
              <router-link to="/profile?update=required" class="text-decoration-underline" style="color: #d97706;">Update your details</router-link>
              to receive important notifications.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Welcome Hero -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card border-0 overflow-hidden" style="background: linear-gradient(135deg, #0891b2 0%, #06b6d4 50%, #22d3ee 100%); border-radius: 16px;">
          <div class="card-body py-4 px-4">
            <div class="row align-items-center">
              <div class="col-lg-8">
                <div class="d-flex align-items-center mb-2">
                  <span class="badge bg-white bg-opacity-25 text-white px-3 py-2 rounded-pill f-s-12 fw-semibold">
                    <i class="ph-duotone ph-star me-1"></i> Sheria Housing Member
                  </span>
                </div>
                <h3 class="text-white fw-bold mb-2" style="letter-spacing: -0.3px;">
                  Welcome back, <span style="text-transform: capitalize;">{{ store.userName }}</span>
                </h3>
                <p class="text-white-50 mb-3" style="max-width: 500px; line-height: 1.6;">
                  Here's an overview of your cooperative accounts. Track your savings, share capital, and land investments all in one place.
                </p>
                <div class="d-flex gap-2 flex-wrap">
                  <router-link to="/transactions" class="btn btn-light btn-sm fw-semibold" style="border-radius: 10px; padding: 8px 16px;">
                    <i class="ph-duotone ph-money me-1"></i> Make Payment
                  </router-link>
                  <router-link to="/profile" class="btn btn-outline-light btn-sm fw-semibold" style="border-radius: 10px; padding: 8px 16px;">
                    <i class="ph-duotone ph-user-circle me-1"></i> My Profile
                  </router-link>
                  <button class="blur-toggle-btn" @click="store.toggleBalances()">
                    <i :class="store.balancesHidden ? 'ph-duotone ph-eye-slash' : 'ph-duotone ph-eye'"></i>
                    {{ store.balancesHidden ? 'Show' : 'Hide' }} Balances
                  </button>
                </div>
              </div>
              <div class="col-lg-4 d-none d-lg-flex justify-content-end">
                <div class="text-center">
                  <p class="text-white-50 f-s-13 mb-1">Total Portfolio Value</p>
                  <h2 class="text-white fw-bold mb-0" :class="store.balancesHidden ? 'balance-blurred' : 'balance-visible'" style="font-size: 2rem; letter-spacing: -1px;">
                    KES {{ totalPortfolio.toLocaleString() }}
                  </h2>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="row mb-4" v-if="!isLoading">
      <div class="col-lg-4 col-sm-6 mb-3 mb-lg-0">
        <div class="card border-0 h-100" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
          <div class="card-body">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <div class="rounded-3 d-flex align-items-center justify-content-center" 
                   style="width: 48px; height: 48px; background: linear-gradient(135deg, #10b981, #34d399);">
                <i class="ph-duotone ph-piggy-bank text-white f-s-22"></i>
              </div>
              <span class="badge bg-light text-success rounded-pill px-3 py-2 f-s-12 fw-semibold">
                <i class="ph-bold ph-trend-up me-1"></i> Active
              </span>
            </div>
            <p class="text-muted mb-1 f-s-13 fw-medium">Deposit Contribution</p>
            <h3 class="fw-bold mb-0" :class="store.balancesHidden ? 'balance-blurred' : 'balance-visible'" style="color: #0f172a; letter-spacing: -0.5px;">
              KES {{ depositTotal.toLocaleString() }}
            </h3>
          </div>
        </div>
      </div>

      <div class="col-lg-4 col-sm-6 mb-3 mb-lg-0">
        <div class="card border-0 h-100" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
          <div class="card-body">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <div class="rounded-3 d-flex align-items-center justify-content-center" 
                   style="width: 48px; height: 48px; background: linear-gradient(135deg, #0891b2, #06b6d4);">
                <i class="ph-duotone ph-coin text-white f-s-22"></i>
              </div>
              <span class="badge bg-light text-primary rounded-pill px-3 py-2 f-s-12 fw-semibold">
                <i class="ph-bold ph-trend-up me-1"></i> Active
              </span>
            </div>
            <p class="text-muted mb-1 f-s-13 fw-medium">Share Capital</p>
            <h3 class="fw-bold mb-0" :class="store.balancesHidden ? 'balance-blurred' : 'balance-visible'" style="color: #0f172a; letter-spacing: -0.5px;">
              KES {{ shareCapitalTotal.toLocaleString() }}
            </h3>
          </div>
        </div>
      </div>

      <div class="col-lg-4 col-sm-12 mb-3 mb-lg-0">
        <div class="card border-0 h-100" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
          <div class="card-body">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <div class="rounded-3 d-flex align-items-center justify-content-center" 
                   style="width: 48px; height: 48px; background: linear-gradient(135deg, #f59e0b, #fbbf24);">
                <i class="ph-duotone ph-house-line text-white f-s-22"></i>
              </div>
              <span class="badge bg-light text-warning rounded-pill px-3 py-2 f-s-12 fw-semibold">
                <i class="ph-bold ph-trend-up me-1"></i> Active
              </span>
            </div>
            <p class="text-muted mb-1 f-s-13 fw-medium">Plot Investments</p>
            <h3 class="fw-bold mb-0" :class="store.balancesHidden ? 'balance-blurred' : 'balance-visible'" style="color: #0f172a; letter-spacing: -0.5px;">
              KES {{ landInstallmentsTotal.toLocaleString() }}
            </h3>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading skeleton -->
    <div class="row mb-4" v-else>
      <div class="col-lg-4 col-sm-6 mb-3" v-for="n in 3" :key="n">
        <div class="card border-0" style="border-radius: 14px;">
          <div class="card-body">
            <div class="placeholder-glow">
              <span class="placeholder col-3 rounded mb-3" style="height: 48px;"></span>
              <span class="placeholder col-5 rounded mb-2"></span>
              <span class="placeholder col-8 rounded" style="height: 28px;"></span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card border-0" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
          <div class="card-body py-3">
            <div class="row">
              <div class="col-6 col-md-3 text-center py-2" v-for="action in quickActions" :key="action.path">
                <router-link :to="action.path" class="text-decoration-none">
                  <div class="rounded-3 d-flex align-items-center justify-content-center mx-auto mb-2"
                       :style="'width: 44px; height: 44px; background:' + action.bg">
                    <i :class="action.icon + ' text-white f-s-20'"></i>
                  </div>
                  <p class="mb-0 fw-medium f-s-13 text-dark">{{ action.label }}</p>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Portfolio Value -->
    <div class="d-lg-none mb-4">
      <div class="card border-0" style="border-radius: 14px; background: linear-gradient(135deg, #0f172a, #1e293b);">
        <div class="card-body text-center py-3">
          <p class="text-white-50 f-s-13 mb-1">Total Portfolio Value</p>
          <h2 class="text-white fw-bold mb-0" :class="store.balancesHidden ? 'balance-blurred' : 'balance-visible'" style="letter-spacing: -0.5px;">KES {{ totalPortfolio.toLocaleString() }}</h2>
        </div>
      </div>
    </div>

    <!-- Recent Transactions -->
    <div class="row">
      <div class="col-12">
        <div class="card border-0" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
          <div class="card-header bg-white border-0 pt-4 pb-2 px-4" style="border-radius: 14px 14px 0 0;">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h5 class="fw-bold mb-0" style="color: #0f172a;">Recent Transactions</h5>
                <small class="text-muted">Your latest financial activity</small>
              </div>
              <router-link to="/transactions" class="btn btn-sm btn-light fw-semibold" style="border-radius: 10px;">
                View All <i class="ph-bold ph-arrow-right ms-1"></i>
              </router-link>
            </div>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover align-middle mb-0">
                <thead>
                  <tr style="background: #f8fafc;">
                    <th class="border-0 f-s-13 fw-semibold text-muted px-4 py-3">Reference</th>
                    <th class="border-0 f-s-13 fw-semibold text-muted py-3">Amount</th>
                    <th class="border-0 f-s-13 fw-semibold text-muted py-3">Account</th>
                    <th class="border-0 f-s-13 fw-semibold text-muted py-3">Date</th>
                    <th class="border-0 f-s-13 fw-semibold text-muted py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="isLoading">
                    <td colspan="5" class="text-center py-5">
                      <div class="spinner-border text-primary spinner-border-sm" role="status"></div>
                      <p class="text-muted mb-0 mt-2 f-s-13">Loading transactions...</p>
                    </td>
                  </tr>
                  <tr v-else-if="recentTransactions.length === 0">
                    <td colspan="5" class="text-center py-5">
                      <i class="ph-duotone ph-receipt f-s-30 text-muted"></i>
                      <p class="text-muted mb-0 mt-2">No transactions found</p>
                    </td>
                  </tr>
                  <tr v-for="t in recentTransactions" :key="t.id" style="cursor: default;">
                    <td class="px-4 py-3">
                      <div class="d-flex align-items-center">
                        <div class="rounded-2 d-flex align-items-center justify-content-center me-3" 
                             :style="'width: 36px; height: 36px; flex-shrink: 0; background:' + getAccountColor(t.account_type)">
                          <i :class="getAccountIcon(t.account_type)" class="text-white f-s-16"></i>
                        </div>
                        <div>
                          <p class="mb-0 fw-semibold f-s-13 text-dark">{{ t.transaction_reference || t.id }}</p>
                          <small class="text-muted f-s-12">{{ t.description || t.transaction_type }}</small>
                        </div>
                      </div>
                    </td>
                    <td class="py-3">
                      <span class="fw-bold f-s-14" :class="store.balancesHidden ? 'balance-blurred' : 'balance-visible'" style="color: #10b981;">KES {{ Number(t.amount).toLocaleString() }}</span>
                    </td>
                    <td class="py-3">
                      <span class="badge rounded-pill px-3 py-2 f-s-11" :style="'background:' + getAccountBadgeBg(t.account_type) + '; color:' + getAccountBadgeColor(t.account_type)">
                        {{ formatAccountType(t.account_type) }}
                      </span>
                    </td>
                    <td class="py-3">
                      <span class="text-muted f-s-13">{{ formatDate(t.created_at) }}</span>
                    </td>
                    <td class="py-3">
                      <span class="badge rounded-pill px-3 py-2 f-s-11" 
                            :class="t.transaction_status === 'completed' ? 'bg-light text-success' : 'bg-light text-warning'">
                        <i :class="t.transaction_status === 'completed' ? 'ph-bold ph-check-circle' : 'ph-bold ph-clock'" class="me-1"></i>
                        {{ t.transaction_status }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '@/components/AppLayout.vue';
import api from '@/services/axios.js';
import { useUserStore } from '@/store';

export default {
  name: 'DashboardPage',
  components: { AppLayout },
  data() {
    return {
      transactions: [],
      shareCapitalTotal: 0,
      depositTotal: 0,
      landInstallmentsTotal: 0,
      isLoading: true,
      quickActions: [
        { path: '/transactions', label: 'Pay Now', icon: 'ph-duotone ph-money', bg: 'linear-gradient(135deg, #10b981, #34d399)' },
        { path: '/projects', label: 'Projects', icon: 'ph-duotone ph-buildings', bg: 'linear-gradient(135deg, #0891b2, #06b6d4)' },
        { path: '/plots', label: 'Plots', icon: 'ph-duotone ph-map-pin-line', bg: 'linear-gradient(135deg, #f59e0b, #fbbf24)' },
        { path: '/self-sort', label: 'Self Service', icon: 'ph-duotone ph-clipboard-text', bg: 'linear-gradient(135deg, #ec4899, #f472b6)' },
      ],
    };
  },
  computed: {
    store() {
      return useUserStore();
    },
    totalPortfolio() {
      return this.depositTotal + this.shareCapitalTotal + this.landInstallmentsTotal;
    },
    profileIncomplete() {
      const email = this.store.userEmail || '';
      return !email || email.includes('temp_email_') || email.includes('@example.com');
    },
    recentTransactions() {
      return [...this.transactions]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 10);
    },
  },
  mounted() {
    this.fetchTransactions();
  },
  methods: {
    async fetchTransactions() {
      try {
        this.isLoading = true;
        const memberId = this.store.memberId;
        if (!memberId) return;

        const response = await api.get(`/api/members/${memberId}/transactions`);
        this.transactions = response.data.transactions || [];

        this.shareCapitalTotal = this.transactions
          .filter(t => t.account_type === 'share_capital' && t.transaction_status === 'completed')
          .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);

        this.depositTotal = this.transactions
          .filter(t => t.account_type === 'savings' && t.transaction_status === 'completed')
          .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);

        this.landInstallmentsTotal = this.transactions
          .filter(t => t.account_type === 'land_installments' && t.transaction_status === 'completed')
          .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);

      } catch (error) {
        console.error('Error fetching transactions:', error);
      } finally {
        this.isLoading = false;
      }
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    formatAccountType(type) {
      const map = { share_capital: 'Share Capital', savings: 'Deposit Contribution', land_installments: 'Plot Investments' };
      return map[type] || type;
    },
    getAccountIcon(type) {
      const map = { share_capital: 'ph-duotone ph-coin', savings: 'ph-duotone ph-piggy-bank', land_installments: 'ph-duotone ph-house-line' };
      return map[type] || 'ph-duotone ph-money';
    },
    getAccountColor(type) {
      const map = { share_capital: 'linear-gradient(135deg, #0891b2, #06b6d4)', savings: 'linear-gradient(135deg, #10b981, #34d399)', land_installments: 'linear-gradient(135deg, #f59e0b, #fbbf24)' };
      return map[type] || '#64748b';
    },
    getAccountBadgeBg(type) {
      const map = { share_capital: '#ecfeff', savings: '#ecfdf5', land_installments: '#fefce8' };
      return map[type] || '#f1f5f9';
    },
    getAccountBadgeColor(type) {
      const map = { share_capital: '#0891b2', savings: '#10b981', land_installments: '#f59e0b' };
      return map[type] || '#64748b';
    },
  },
};
</script>